/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage } from '@hookform/error-message';
import useListenSize from '../hooks/useListenSize';
import birthDateMask from '../helpers/birthDateMask';
import ssnMask from '../helpers/ssnMask';
import statesMapping from '../helpers/statesMapping';
import { addNewUserDetails } from '../redux/actions';
import SubmitButton from '../components/SubmitButton';
import StatesSelect from '../components/StatesSelect';

function NewUserDetails() {
  const store = useSelector(state => ({
    checkoutLookupData: state.personal?.checkoutLookupData?.checkout_object?.customer.billing,
    newUserDetails: state.personal?.newUserDetails,
    sessionId: state.technical?.shopifySessionId,
    orgId: state.technical?.shopifyOrgId,
    isInstoreVccFlow: state.technical?.instoreVccFlow?.isInstoreVccFlow,
  }));
  // Shopify pre-fills these from the checkout; in-store VCC has no cart yet, so the customer enters them.
  const isPrefilledLocked = !store.isInstoreVccFlow;

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const history = useHistory();
  const dispatch = useDispatch();
  useListenSize();

  const [birthDate, setBirthDate] = useState('');
  const [ssn, setSsn] = useState('');
  const [yearMoreThan100, setYearMoreThan100] = useState(false);

  useEffect(() => {
    setValue('billing_first_name', store.newUserDetails?.billing_first_name || store.checkoutLookupData?.first_name);
    setValue('billing_last_name', store.newUserDetails?.billing_last_name || store.checkoutLookupData?.last_name);
    setValue('email', store.newUserDetails?.email || store.checkoutLookupData?.email);
    setValue('billing_address', store.newUserDetails?.billing_address || store.checkoutLookupData?.address);
    setValue('billing_address2', store.newUserDetails?.billing_address2 || store.checkoutLookupData?.address2);
    setValue('billing_city', store.newUserDetails?.billing_city || store.checkoutLookupData?.city);
    setValue('billing_zip', store.newUserDetails?.billing_zip || store.checkoutLookupData?.zip);
    setValue('dob_day', store.newUserDetails?.dob_day);
    setValue('ssn', store.newUserDetails?.ssn);
    setValue('billing_state', store.newUserDetails?.billing_state || statesMapping(store.checkoutLookupData?.state));
  }, []);

  useEffect(() => {
    window.threatmetrix?.profile('tmx.katapult.com', store?.orgId, store?.sessionId, 2002);
    console.log('loaded 2002');
  }, []);

  const formatBirthDate = value => {
    setBirthDate(birthDateMask(value));
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    if (value.length === 10) {
      const dateObj = new Date(value);
      const year = dateObj.getFullYear();
      if (year < currentYear - 100) {
        setYearMoreThan100(true);
        setError('dob_day', { type: 'year', message: 'Invalid date of birth' });
      } else {
        setYearMoreThan100(false);
        clearErrors('dob_day');
      }
    }
  };

  const onSubmit = async values => {
    formatBirthDate(values.dob_day);
    dispatch(addNewUserDetails(values));
    if (yearMoreThan100) {
      return;
    }
    history.push('/new-user-income');
  };

  const formatSsn = ssnValue => {
    setSsn(ssnMask(ssnValue));
  };

  return (
    <div className="k-content-wrapper">
      <section className="k-form k-form-indent">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="billing_first_name">First name</label>
            <input
              type="text"
              name="billing_first_name"
              id="billing_first_name"
              placeholder="Riley"
              disabled={isPrefilledLocked}
              {...register('billing_first_name', {
                required: 'This is required',
                pattern: {
                  value: /^[A-Za-z]+$/,
                  message: 'Invalid name',
                },
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_first_name" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="billing_last_name">Last name</label>
            <input
              type="text"
              name="billing_last_name"
              id="billing_last_name"
              placeholder="Addison"
              disabled={isPrefilledLocked}
              {...register('billing_last_name', {
                required: 'This is required',
                pattern: {
                  value: /^[A-Za-z]+$/,
                  message: 'Invalid name',
                },
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_last_name" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="dob_day">Date of birth</label>
            <input
              type="text"
              name="dob_day"
              id="dob_day"
              value={birthDate}
              placeholder="06/15/1981"
              {...register('dob_day', {
                required: 'This is required',
                pattern: {
                  value: /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19\d{2}|20\d{2})$/,
                  message: 'Should follow correct date format - example 06/15/1981',
                },
                onChange: e => formatBirthDate(e.target.value),
                onBlur: e => formatBirthDate(e.target.value),
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="dob_day" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="ssn">SSN/ITIN</label>
            <input
              type="text"
              name="ssn"
              value={ssn}
              id="ssn"
              placeholder="123-45-6789"
              className="fs-exclude"
              {...register('ssn', {
                required: 'This is required',
                pattern: {
                  value: /\d{3}-\d{2}-\d{4}/,
                  message: 'Should follow format 123-45-6789',
                },
                onChange: e => formatSsn(e.target.value),
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="ssn" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="code">Email</label>
            <input
              type="email"
              name="email"
              id="email"
              placeholder="railyaddison@katapult.com"
              disabled={isPrefilledLocked}
              {...register('email', {
                required: 'This is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="email" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="code">Billing address</label>
            <input
              type="text"
              name="billing_address"
              id="billing_address"
              placeholder="12345 Mockingjay street"
              disabled={isPrefilledLocked}
              {...register('billing_address', {
                required: 'This is required',
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_address" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="billing_address2">Address 2</label>
            <input
              type="text"
              name="billing_address2"
              id="billing_address2"
              placeholder="Apt 5"
              disabled={isPrefilledLocked}
              {...register('billing_address2')}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_address2" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="billing_city">City</label>
            <input
              type="text"
              name="billing_city"
              id="billing_city"
              placeholder="New York"
              disabled={isPrefilledLocked}
              {...register('billing_city', {
                required: 'This is required',
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_city" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="state">State</label>
            <select
              name="billing_state"
              disabled={isPrefilledLocked}
              {...register('billing_state', {
                required: 'This is required',
              })}
            >
              <StatesSelect />
            </select>
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_state" render={({ message }) => message} />
            </div>
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="billing_zip">Zip code</label>
            <input
              type="tel"
              name="billing_zip"
              maxLength="5"
              id="zip"
              placeholder="11111"
              disabled={isPrefilledLocked}
              {...register('billing_zip', {
                required: 'This is required',
                pattern: {
                  value: /\d{5}/,
                  message: 'Should be 5 numeric digits ',
                },
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="billing_zip" render={({ message }) => message} />
            </div>
          </div>
          <SubmitButton />
        </form>
      </section>
    </div>
  );
}

export default NewUserDetails;
